import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Grid } from '@mui/material';
import { FC, useState } from 'react';
import { ContactType, SendOtpRes } from '~/api/types';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import { ContactSelectionButton } from '../button/contact-selection-button';
import { ErrorScreen } from './error';
import { LoadingScreen } from './loading';
import { isAxiosError } from 'axios';

const CONTACT_TYPES: { type: ContactType; label: string }[] = [
  { type: 'whatsApp', label: 'WhatsApp' },
  { type: 'email', label: 'E-mail' },
];

/**
 * For a document whose developments the backend did not return: nothing about
 * its contacts is known, so the customer only picks the type of the one the otp
 * goes to. The backend answers the same whether or not it was sent.
 */
export const ContactTypeSelectionScreen: FC<ContactTypeSelectionScreenProps> = ({
  onOtpSent,
  goBack,
  sendOtp,
  title = 'Escolha como receber seu acesso',
  description = 'Selecione abaixo por onde quer receber o código de acesso a sua área do cliente. Ele será enviado ao contato cadastrado no seu contrato.',
}) => {
  const [reqStatus, setReqStatus] = useState<ReqStatus>('idle');
  const [selectedContactType, setSelectedContactType] = useState<ContactType | null>(null);
  const errorDescription = isReqError(reqStatus) ? getErrorMessageFromReqStatus(reqStatus) : null;

  const sendOtpMessage = async () => {
    if (!selectedContactType) return;
    setReqStatus('in-progress');
    try {
      const { otpId } = await sendOtp(selectedContactType);
      onOtpSent(selectedContactType, otpId);
    } catch (error: unknown) {
      console.error(error);
      if (isAxiosError(error) && error.response?.status === 429) {
        setReqStatus('too-many-requests');
      } else {
        setReqStatus('error');
      }
    }
  };

  if (reqStatus === 'in-progress') {
    return <LoadingScreen message="Enviando acesso" />;
  }

  if (errorDescription) {
    return (
      <ErrorScreen
        title={errorDescription.title}
        message={errorDescription.message}
        refetch={sendOtpMessage}
        cancel={() => setReqStatus('idle')}
      />
    );
  }

  return (
    <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
      <Title>{title}</Title>
      <Body1>{description}</Body1>
      <Box>
        {CONTACT_TYPES.map((c) => (
          <ContactSelectionButton
            key={c.type}
            contactType={c.type}
            label={c.label}
            onClick={() => setSelectedContactType(c.type)}
            selected={selectedContactType === c.type}
          />
        ))}
      </Box>
      <Grid container>
        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }} sx={{ textAlign: { xs: 'center', md: 'left' }, mt: 2 }}>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Grid>
        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'right' }, mt: 2 }}>
          <Button
            disabled={!selectedContactType}
            onClick={sendOtpMessage}
            variant="contained"
            fullWidth
            sx={{ fontWeight: 'bold' }}
          >
            Enviar código
          </Button>
        </Grid>
      </Grid>
    </Page>
  );
};

interface ContactTypeSelectionScreenProps {
  title?: string;
  description?: string;
  onOtpSent: (contactType: ContactType, otpId: string) => void;
  goBack: () => void;
  sendOtp: (contactType: ContactType) => Promise<SendOtpRes>;
}

type ReqStatus = 'idle' | 'in-progress' | ReqError;
type ReqError = 'too-many-requests' | 'error';

function isReqError(reqStatus: ReqStatus): reqStatus is ReqError {
  return ['too-many-requests', 'error'].includes(reqStatus);
}

function getErrorMessageFromReqStatus(reqStatus: ReqError): ErrorDesciption {
  const errorMap: Record<ReqError, ErrorDesciption> = {
    'too-many-requests': {
      title: 'Muitas tentativas',
      message: 'Você atingiu o limite de envios. Tente novamente mais tarde.',
    },
    'error': {
      title: 'Erro de envio',
      message: 'Houve um problema para enviar seu acesso.\nPor favor, tente novamente.',
    },
  };
  return errorMap[reqStatus];
}

interface ErrorDesciption {
  title: string;
  message: string;
}
